import {
  signIn,
  signUpUser,
  verifyCode,
  resendVerificationCode,
  signOut,
  sendResetCode,
  confirmResetPassword,
  changePassword,
  getAttributes,
  updateAttributes,
  getCurrentUser,
  ensureValidSession,
} from "../services/cognito";
import {
  AppUserProfile,
  getCurrentUserProfile,
  updateUserProfile,
  verifyUser,
} from "../services/auth";

import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
  ReactNode,
} from "react";
import {
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import Cookies from "js-cookie";
import { AuthContextModel } from "../model/AuthContextModel";
import { ID_TOKEN_KEY, USER_ID_KEY } from "../helpers/constant";

const AuthContext = createContext<AuthContextModel | undefined>(undefined);

const getAttributeValue = (
  attributes: CognitoUserAttribute[] | undefined,
  attributeName: string,
): string | null => {
  if (!attributes) return null;
  const found = attributes.find((attr) => attr.getName() === attributeName);
  return found ? found.getValue() : null;
};

const isAdminOriginRequest = (): boolean => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("origin") === "admin";
};

const redirectToAdminWithToken = (token: string): void => {
  if (typeof window === "undefined" || !token) return;

  const dashboardBase = process.env.REACT_APP_DASHBOARD || "/admin";
  const dashboardUrl = new URL(dashboardBase, window.location.origin);
  dashboardUrl.searchParams.set("token", token);
  window.location.assign(dashboardUrl.toString());
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [appUserProfile, setAppUserProfile] = useState<AppUserProfile>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isBootstrapping, setIsBootstrapping] = useState<boolean>(true);
  const [idToken, setIdToken] = useState<string | undefined>(
    localStorage.getItem(ID_TOKEN_KEY) ?? undefined,
  );
  const [userId, setUserId] = useState<string | undefined>(
    localStorage.getItem(USER_ID_KEY) ?? undefined,
  );

  const persistAuthSession = useCallback(
    (session: CognitoUserSession, userId?: string) => {
      const token = session.getIdToken().getJwtToken();
      const idPayload = session.getIdToken().decodePayload() as {
        sub?: string;
        exp?: number;
      };
      const resolvedUserId = idPayload.sub ?? userId;

      setIdToken(token);
      localStorage.setItem(ID_TOKEN_KEY, token);
      // Cookies.set(ID_TOKEN_COOKIE_KEY, token);

      if (resolvedUserId) {
        setUserId(resolvedUserId);
        localStorage.setItem(USER_ID_KEY, resolvedUserId);
        // Cookies.set(USER_ID_COOKIE_KEY, resolvedUserId);
      }

      if (idPayload.exp) {
        const expiryDate = new Date(idPayload.exp * 1000);
        // Cookies.set(ID_TOKEN_COOKIE_KEY, token, { expires: expiryDate });
        if (resolvedUserId) {
          // Cookies.set(USER_ID_COOKIE_KEY, resolvedUserId, {
          //   expires: expiryDate,
          // });
        }
      }
    },
    [],
  );

  const clearPersistedAuth = useCallback(() => {
    setIdToken(undefined);
    setUserId(undefined);
    setAppUserProfile(undefined);
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
    // Cookies.remove(ID_TOKEN_COOKIE_KEY);
    // Cookies.remove(USER_ID_COOKIE_KEY);
  }, []);

  const isUnauthorizedApiError = (error: unknown): boolean => {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    return status === 401 || status === 403;
  };

  const getUserProfileInfo = useCallback(async (): Promise<AppUserProfile> => {
    const profile = await getCurrentUserProfile(
      userId ?? localStorage.getItem(USER_ID_KEY)!,
    );
    setAppUserProfile(profile);
    return profile;
  }, [userId]);

  // Helper to wrap all async calls with a single isLoading
  const withLoading = useCallback(
    async <T,>(callback: () => Promise<T>): Promise<T> => {
      try {
        setIsLoading(true);
        return await callback();
      } catch (e) {
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const refreshUserAttributes = useCallback(async () => {
    await withLoading(async () => {
      const attributes = (await getAttributes()) as
        | CognitoUserAttribute[]
        | undefined;
      const sub = getAttributeValue(attributes, "sub");
      if (sub) {
        setUserId(sub);
        localStorage.setItem(USER_ID_KEY, sub);
        // Cookies.set(USER_ID_COOKIE_KEY, sub);
      }
    });
  }, [withLoading]);

  const signOutHandler = useCallback(
    async () =>
      await withLoading(async () => {
        try {
          await signOut();
        } catch (error) {
          const message =
            error instanceof Error ? error.message.toLowerCase() : "";
          if (!message.includes("not authenticated")) {
            throw error;
          }
        }
        clearPersistedAuth();
        setAppUserProfile(undefined);
        setIsAuthenticated(false);
      }),
    [clearPersistedAuth, withLoading],
  );

  const signInHandler = useCallback(
    async (email: string, password: string) =>
      await withLoading(async () => {
        const session = await signIn(email, password);
        console.log("Session after sign in:", session);
        const currentUser = getCurrentUser();
        console.log("Current user after sign in:", currentUser);

        persistAuthSession(session, currentUser?.getUsername());

        const token = session.getIdToken().getJwtToken();

        try {
          const profile = await getUserProfileInfo();
          console.log("User profile after sign in:", profile);
          if (profile.status === "blocked") {
            await signOutHandler();
            throw new Error("Your account is blocked. Please contact support.");
          }

          if (isAdminOriginRequest()) {
            redirectToAdminWithToken(token);
          }

          return profile;
        } catch (profileError) {
          console.error(
            "Error fetching user profile after sign in:",
            profileError,
          );
          if (isUnauthorizedApiError(profileError)) {
            await signOutHandler();
            throw new Error("Your account is not authorized.");
          }
          throw profileError;
        } finally {
          setIsAuthenticated(true);
        }
      }),
    [persistAuthSession, getUserProfileInfo, withLoading, signOutHandler],
  );

  const signUpHandler = useCallback(
    async (
      username: string,
      email: string,
      password: string,
      phoneNumbers: string,
      givenName: string,
      familyName: string,
      city: string,
      lang: string,
    ) =>
      await withLoading(async () => {
        const response = await signUpUser(
          username,
          email,
          password,
          phoneNumbers,
          givenName,
          familyName,
          { city, lang },
        );
        return response.userSub;
      }),
    [withLoading],
  );

  const verifyCodeHandler = useCallback(
    async (email: string, code: string) =>
      await withLoading(async () => {
        await verifyCode(email, code);
        await verifyUser(email);
      }),
    [withLoading],
  );

  const resendVerificationCodeHandler = useCallback(
    async (email: string) =>
      await withLoading(async () => {
        await resendVerificationCode(email);
      }),
    [withLoading],
  );

  const sendResetCodeHandler = useCallback(
    async (email: string, lang: string) =>
      await withLoading(async () => {
        await sendResetCode(email, lang);
      }),
    [withLoading],
  );

  const confirmResetPasswordHandler = useCallback(
    async (email: string, code: string, newPass: string) =>
      await withLoading(async () => {
        await confirmResetPassword(email, code, newPass);
      }),
    [withLoading],
  );

  const changePasswordHandler = useCallback(
    async (oldPassword: string, newPassword: string) =>
      await withLoading(async () => {
        await changePassword(oldPassword, newPassword);
      }),
    [withLoading],
  );

  const updateAttributesHandler = useCallback(
    async (attrs: Record<string, string>) =>
      await withLoading(async () => {
        await updateAttributes(attrs);
        const resolvedUserId = userId ?? localStorage.getItem(USER_ID_KEY);

        if (resolvedUserId) {
          const updatedProfile = await updateUserProfile(resolvedUserId, {
            userID: resolvedUserId,
            firstname: attrs.given_name ?? appUserProfile?.firstname ?? "",
            lastname: attrs.family_name ?? appUserProfile?.lastname ?? "",
            phoneNumber:
              attrs.phone_number ?? appUserProfile?.phoneNumber ?? "",
            email: attrs.email ?? appUserProfile?.email ?? "",
            city: (
              attrs.zoneinfo ??
              appUserProfile?.city ??
              "douala"
            ).toLowerCase(),
            lang: (attrs.locale ?? appUserProfile?.lang ?? "fr").toLowerCase(),
          });
          setAppUserProfile(updatedProfile);
        }
        await refreshUserAttributes();

        if (!resolvedUserId) {
          await getUserProfileInfo();
        }
      }),
    [
      appUserProfile,
      getUserProfileInfo,
      refreshUserAttributes,
      userId,
      withLoading,
    ],
  );

  // Load User Session on App Start
  const loadSession = useCallback(async () => {
    setIsBootstrapping(true);
    try {
      const current = getCurrentUser();
      const token = localStorage.getItem(ID_TOKEN_KEY);
      const storedUserId = localStorage.getItem(USER_ID_KEY);
      if (!current || !token || !storedUserId) {
        clearPersistedAuth();
        setIsAuthenticated(false);
        return;
      }

      const session = await ensureValidSession();
      if (session.isValid()) {
        const currentUser = getCurrentUser();
        persistAuthSession(session, currentUser?.getUsername());
        const token = session.getIdToken().getJwtToken();

        try {
          const profile = await getUserProfileInfo();
          if (profile.status === "blocked") {
            await signOutHandler();
            return;
          }

          if (isAdminOriginRequest()) {
            redirectToAdminWithToken(token);
            return;
          }
        } catch (profileError) {
          if (isUnauthorizedApiError(profileError)) {
            await signOutHandler();
            return;
          }
          throw profileError;
        }

        setIsAuthenticated(true);
      }
    } catch (err) {
      console.warn("Failed to load session", err);
      clearPersistedAuth();
      setIsAuthenticated(false);
    } finally {
      setIsBootstrapping(false);
    }
  }, [
    clearPersistedAuth,
    persistAuthSession,
    signOutHandler,
    getUserProfileInfo,
  ]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const value: AuthContextModel = useMemo(
    () => ({
      appUserProfile,
      isAuthenticated,
      isLoading,
      isBootstrapping,
      idToken,
      userId,
      signInHandler,
      getUserProfileInfo,
      signUpHandler,
      verifyCodeHandler,
      resendVerificationCodeHandler,
      signOutHandler,
      sendResetCodeHandler,
      confirmResetPasswordHandler,
      changePasswordHandler,
      refreshUserAttributes,
      updateAttributesHandler,
    }),
    [
      idToken,
      appUserProfile,
      changePasswordHandler,
      confirmResetPasswordHandler,
      isAuthenticated,
      isBootstrapping,
      isLoading,
      getUserProfileInfo,
      refreshUserAttributes,
      sendResetCodeHandler,
      signInHandler,
      signOutHandler,
      signUpHandler,
      resendVerificationCodeHandler,
      updateAttributesHandler,

      userId,
      verifyCodeHandler,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook
export const useAuth = (): AuthContextModel => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
